import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { apiService } from '../services/api';
import { useTranslation } from 'react-i18next';
import './CSS/conversations.css';
import './CSS/marketplace-messaging.css';
import { useConversations } from '../hooks/useConversations';
import { formatDate } from '../utils/helpers';
import { LoadingSpinner, EmptyState } from '../componnents/UI';
import MarketplaceChatPane from '../componnents/MarketplaceChatPane/MarketplaceChatPane';
import ProductContextPanel from '../componnents/marketplace/ProductContextPanel';
import ConversationListItem from '../componnents/marketplace/ConversationListItem';
import { ArrowLeft } from 'lucide-react';

const Conversations = () => {
  const { t } = useTranslation('conversations');
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const routeState = location.state || {};

  const {
    conversations,
    isLoading,
    refetch,
    getConversationId,
    getPreviewProduct,
  } = useConversations();

  const [selectedId, setSelectedId] = useState(routeState.conversationId || null);
  const [activeConversation, setActiveConversation] = useState(null);
  const [focusOffer, setFocusOffer] = useState(Boolean(routeState.openOffer));

  const clearRouteState = useCallback(() => {
    window.history.replaceState({}, '', '/conversations');
    setFocusOffer(false);
  }, []);

  const openStoreFromRoute = useCallback(async () => {
    const sellerId = routeState.sellerId;
    if (!sellerId || !isAuthenticated) return;
    try {
      const response = await apiService.ensureStoreConversation(sellerId);
      if (response.status === 'success' && response.data) {
        const id = response.data.id || response.data._id;
        if (id) {
          setSelectedId(id);
          setActiveConversation(response.data);
          clearRouteState();
        }
      }
    } catch (err) {
      const code = err?.response?.data?.code || err?.code;
      if (code !== 'OWN_STORE') console.warn('Store conversation open failed:', err);
    }
  }, [routeState.sellerId, isAuthenticated, clearRouteState]);

  const openProductFromRoute = useCallback(async () => {
    const productId = routeState.productId;
    if (!productId || !isAuthenticated) return;

    if (routeState.conversationId) {
      setSelectedId(routeState.conversationId);
      if (focusOffer) setFocusOffer(true);
      clearRouteState();
      return;
    }

    try {
      const response = await apiService.ensureProductConversation(productId);
      if (response.status === 'success' && response.data) {
        const id = response.data.id || response.data._id;
        if (id) {
          setSelectedId(id);
          setActiveConversation(response.data);
          setFocusOffer(Boolean(routeState.openOffer));
          clearRouteState();
        }
      }
    } catch (err) {
      const code = err?.response?.data?.code || err?.code;
      if (code !== 'OWN_STORE') console.warn('Product conversation open failed:', err);
    }
  }, [
    routeState.productId,
    routeState.conversationId,
    routeState.openOffer,
    isAuthenticated,
    focusOffer,
    clearRouteState,
  ]);

  useEffect(() => {
    openStoreFromRoute();
  }, [openStoreFromRoute]);

  useEffect(() => {
    openProductFromRoute();
  }, [openProductFromRoute]);

  useEffect(() => {
    if (routeState.conversationId) {
      setSelectedId(routeState.conversationId);
    }
  }, [routeState.conversationId]);

  const getStoreName = (conversation) => {
    const product = getPreviewProduct(conversation);
    const seller = conversation?.seller;
    return seller?.storeName || seller?.name || product?.brand || t('officialStore');
  };

  const previewProduct =
    getPreviewProduct(activeConversation) || getPreviewProduct(
      conversations.find((c) => getConversationId(c) === selectedId)
    );
  const activeOffer = activeConversation?.activeOffer;

  return (
    <div className="conversations-page mp-inbox">
      <header className="mp-inbox__header">
        <button type="button" className="back-button" onClick={() => navigate(-1)}>
          <ArrowLeft size={22} />
        </button>
        <div>
          <h1>{t('title')}</h1>
          <p>{t('subtitle')}</p>
        </div>
      </header>

      <div className="mp-inbox__grid">
        <aside
          className={`mp-panel mp-panel--list ${
            selectedId ? 'mp-panel--list--hidden' : ''
          }`}
        >
          {isLoading ? (
            <div className="conversations-list-loading">
              <LoadingSpinner text={t('loading')} />
            </div>
          ) : conversations.length === 0 ? (
            <EmptyState
              icon="messages"
              title={t('emptyTitle')}
              description={t('emptyDescription')}
              actionText={t('browseProducts')}
              actionLink="/"
            />
          ) : (
            <ul className="mp-conv-list">
              {conversations.map((conversation) => {
                const conversationId = getConversationId(conversation);
                const product = getPreviewProduct(conversation);
                const unread = conversation.unreadByBuyer || 0;
                const storeName = getStoreName(conversation);

                return (
                  <ConversationListItem
                    key={conversationId}
                    conversation={conversation}
                    product={product}
                    storeName={storeName}
                    isSelected={selectedId === conversationId}
                    unread={unread}
                    timeLabel={
                      conversation.lastMessageAt
                        ? formatDate(conversation.lastMessageAt)
                        : ''
                    }
                    preview={conversation.lastMessage || t('noPreview')}
                    onSelect={() => {
                      setSelectedId(conversationId);
                      setActiveConversation(conversation);
                    }}
                  />
                );
              })}
            </ul>
          )}
        </aside>

        <main
          className={`mp-panel mp-panel--chat ${
            selectedId ? 'mp-panel--chat--visible' : ''
          }`}
        >
          {selectedId && previewProduct && (
            <div className="mp-chat__product-sticky">
              <ProductContextPanel
                product={previewProduct}
                seller={activeConversation?.seller}
                activeOffer={activeOffer}
                variant="sticky"
              />
            </div>
          )}
          <MarketplaceChatPane
            conversationId={selectedId}
            focusOffer={focusOffer}
            onBack={() => {
              setSelectedId(null);
              setActiveConversation(null);
            }}
            onConversationUpdate={(conv) => {
              setActiveConversation(conv);
              refetch();
            }}
          />
        </main>

        <aside className="mp-panel mp-panel--details">
          <ProductContextPanel
            product={previewProduct}
            seller={activeConversation?.seller}
            activeOffer={activeOffer}
            variant="panel"
          />
        </aside>
      </div>
    </div>
  );
};

export default Conversations;
